// Contractor notes on a personnel member for VITE_CREDENTIALING_MOCKS. The
// signed-in mock user is Ana Rivera; her notes are the editable ones.
import {
  CreatePersonnelNoteRequest,
  NoteAuditEntry,
  PersonnelNote,
  PersonnelNoteInput,
  UpdatePersonnelNoteRequest,
} from '@src/types/personnel';
import { mockApiError } from '../credentialingContract';

const ME = { name: 'Ana Rivera', role: 'Contractor Credentialing User' };

type MockNote = Omit<PersonnelNote, 'isOwn'> & {
  audit: Array<NoteAuditEntry>;
  personnelId: number;
};

let nextId = 10;

const now = () => {
  const date = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const created = (
  createdAt: string,
  authorName: string,
  authorRole: string,
  text: string
): NoteAuditEntry => ({
  action: 'Created',
  at: createdAt,
  id: nextId++,
  newValue: text,
  originalValue: null,
  role: authorRole,
  userName: authorName,
});

const seed = (
  createdAt: string,
  authorName: string,
  authorRole: string,
  category: PersonnelNote['category'],
  text: string,
  audit: Array<NoteAuditEntry> = [
    created(createdAt, authorName, authorRole, text),
  ]
): MockNote => ({
  audit,
  authorName,
  authorRole,
  category,
  createdAt,
  id: nextId++,
  personnelId: 5,
  text,
});

// prettier-ignore
let notes: Array<MockNote> = [
  seed('10/12/2026 09:14', ME.name, ME.role, 'Credentialing Record Modified', 'Updated primary contact number after Maria called in with her new number.', [
    { action: 'Edited', at: '10/12/2026 09:21', id: nextId++, newValue: 'Updated primary contact number after Maria called in with her new number.', originalValue: 'Updated contact number after Maria called in.', role: ME.role, userName: ME.name },
    created('10/12/2026 09:14', ME.name, ME.role, 'Updated contact number after Maria called in.'),
  ]),
  seed('10/09/2026 16:02', 'James Okafor', 'Contractor User Admin', 'Credentialing Record Note', 'Uploaded Q’Straint wheelchair certificate. Waiting on LA Care and Molina.'),
  seed('03/01/2026 08:30', 'James Okafor', 'Contractor User Admin', 'Credentialing Record Modified', 'Status set to Active for start date. Background check and MVR cleared.'),
];

const toNote = ({
  audit: _audit,
  personnelId: _personnelId,
  ...note
}: MockNote): PersonnelNote => ({
  ...note,
  isOwn: note.authorName === ME.name,
});

const findNote = (personnelId: number, noteId: number) => {
  const note = notes.find(
    (item) => item.id === noteId && item.personnelId === personnelId
  );
  if (!note) throw mockApiError(404, 'Note not found');
  return note;
};

const validText = (text: string) => {
  const length = text.trim().length;
  if (length < 15 || length > 250) {
    throw mockApiError(400, 'A note is 15 to 250 characters');
  }
};

/** the note a profile save requires is kept like any other (WF-D1 card 8) */
export const addNote = (
  personnelId: number,
  { category, text }: PersonnelNoteInput
) => {
  validText(text);
  const createdAt = now();
  const note: MockNote = {
    audit: [created(createdAt, ME.name, ME.role, text)],
    authorName: ME.name,
    authorRole: ME.role,
    category,
    createdAt,
    id: nextId++,
    personnelId,
    text,
  };
  notes = [note, ...notes];
  return toNote(note);
};

export const notesMocks = {
  list: ({ personnelId }: { personnelId: number }) =>
    notes.filter((note) => note.personnelId === personnelId).map(toNote),

  create: ({ note, personnelId }: CreatePersonnelNoteRequest) =>
    addNote(personnelId, note),

  update: ({ noteId, personnelId, text }: UpdatePersonnelNoteRequest) => {
    const note = findNote(personnelId, noteId);
    if (note.authorName !== ME.name)
      throw mockApiError(403, 'Only the author can edit a note');
    validText(text);

    const updated: MockNote = {
      ...note,
      audit: [
        {
          action: 'Edited',
          at: now(),
          id: nextId++,
          newValue: text,
          originalValue: note.text,
          role: ME.role,
          userName: ME.name,
        },
        ...note.audit,
      ],
      text,
    };
    notes = notes.map((item) => (item.id === noteId ? updated : item));
    return toNote(updated);
  },

  audit: ({ noteId, personnelId }: { personnelId: number; noteId: number }) =>
    findNote(personnelId, noteId).audit,
};
